// Drag & Drop in einen Kundenakten-Ordner, der (noch) nicht gebucht ist
// (User 02.11.2026: „das drag and drop von outlook und von mails auch in
// /alte-kundenakte enablen“).
//
// Ist der Ordner gebucht, zeigt die Detailseite die echten Dokumente des
// Kontakts (DocumentsSection) — dort gibt es das Ablegen schon. Die übrigen
// Fälle zeigen die Ablage bzw. den Share-Index: das sind Dateien des alten
// Shares, KEINE Dokumente. Abgelegt wird deshalb beim zugewiesenen Kontakt in
// seiner Alten Kundenakte, im selben Pfad wie der geöffnete Ordner — genau
// dort steht die Datei, sobald die Akte gebucht ist (die Buchung legt die
// Share-Dateien unter ihrem relativen Pfad ab). Ohne Kontakt gibt es kein
// Ziel: Dann sagt die Ablagezone das, statt den Drop still zu verschlucken.

import * as React from "react";
import type { FileExplorerProps } from "../documents/explorer/FileExplorer";
import type { AbgelegteDatei } from "../documents/explorer/dateiDrop";
import { ladeInAkteHoch } from "../documents/akteUpload";
import { fremdAblageArt, fremdAblageWas, leseAblageZug } from "../mail/postfachAblage";
import { ausNeuemOutlook, legeNeuesOutlookAb } from "../mail/neuesOutlook";
import { legeZugAbMitAnhangFrage, nachMailDateiUploadMitFrage } from "../documents/ablage/mailAnhangAblage";
import { useAblagePlatzhalter } from "../documents/ablage/useAblagePlatzhalter";
import { AblagePlatzhalterZeilen } from "../documents/ablage/AblagePlatzhalterAnzeige";
import { Toast } from "../redesign/Toast";
import { contactName, type Folder } from "./types";

/** Kategorie des Altbestands — dieselbe wie in DocumentsSection. */
export const KUNDENAKTE_KATEGORIE = "Alte Kundenakte";

export type AkteAblageZiel = { contactId: number; name: string } | { grund: string };

/**
 * Wohin ein Drop in diesem Ordner geht: der Hauptkontakt; ohne ihn der eine
 * mitzugewiesene Kontakt. Mehrere ohne Hauptkontakt oder gar keiner → kein Ziel.
 */
export function akteAblageZiel(f: Pick<Folder, "contact" | "weitereKontakte">): AkteAblageZiel {
  if (f.contact?.id) return { contactId: f.contact.id, name: contactName(f.contact) };
  const weitere = f.weitereKontakte ?? [];
  if (weitere.length === 1) return { contactId: weitere[0].contactId, name: weitere[0].name || `Kontakt ${weitere[0].contactId}` };
  if (weitere.length > 1) {
    return { grund: "Dieser Ordner hat keinen Hauptkontakt — bitte über „Zuweisung ändern“ einen Hauptkontakt wählen, dann ablegen." };
  }
  return { grund: "Dieser Ordner ist noch keinem Kontakt zugewiesen — bitte zuerst „Zuweisen“, dann ablegen." };
}

/** Explorer-Pfad (`"a/b/"`) → Ordner der Mail-Ablage (`"a/b"`, Wurzel = null). */
export const ablageOrdner = (pfad: string): string | null => pfad.replace(/^\/+|\/+$/g, "") || null;

/** Die Props, die der FileExplorer fürs Ablegen bekommt. */
export type AkteAblageProps = Pick<
  FileExplorerProps<unknown>,
  "onDropFiles" | "dropFilesHinweis" | "fremdAblageHinweis" | "onDropFremd" | "kopfZeilen"
>;

/**
 * Drop-Logik für AblageExplorer und KundenaktenIndexExplorer. `pfad` ist der
 * geöffnete Ordner; Dateien landen in dem Ordner, über dem losgelassen wurde.
 */
export function useAkteAblage(ziel: AkteAblageZiel, pfad: string): { props: AkteAblageProps; anzeige: React.ReactNode } {
  const ablage = useAblagePlatzhalter();
  const [toast, setToast] = React.useState<{ text: string; fehler: boolean } | null>(null);
  const laeuft = React.useRef(false);
  const ordnerName = ablageOrdner(pfad);
  const wo = ordnerName ? `„${ordnerName.split("/").pop()}“` : "die Wurzel der Akte";

  const hinweisErfolg = (name: string, n: number) =>
    `${n === 1 ? "Abgelegt" : `${n} Einträge abgelegt`} — in der Kundenakte von ${name}${
      ordnerName ? `, Ordner „${ordnerName}“` : ""
    }. Hier erscheint es, sobald die Akte gebucht ist.`;

  const hochladen = async (dateien: AbgelegteDatei[], zielPfad: string) => {
    if (!dateien.length) return;
    if ("grund" in ziel) {
      setToast({ text: ziel.grund, fehler: true });
      return;
    }
    if (laeuft.current) {
      setToast({ text: "Es läuft noch ein Upload — bitte warten, bis er fertig ist.", fehler: true });
      return;
    }
    laeuft.current = true;
    const ids = ablage.starte(dateien.map((d) => ({ name: d.file.name, mimeType: d.file.type || null })));
    const auspackFehler: string[] = [];
    // Outlook-Mails mit Anhängen: nach dem ganzen Upload fragen (User 03.11.2026).
    const mailDateien: { datei: File; dokument: { id?: number | null } | null | undefined }[] = [];
    try {
      const res = await ladeInAkteHoch({
        dateien,
        contactId: ziel.contactId,
        basisPfad: zielPfad,
        kategorie: KUNDENAKTE_KATEGORIE,
        onDatei: (i, erg) => {
          // Geglücktes erscheint in dieser Ansicht nicht (sie zeigt den Share) —
          // der Platzhalter geht, die Meldung sagt, wo es liegt.
          if (erg.ok) ablage.weg([ids[i]]);
          else ablage.scheitern(ids[i], erg.nichtLesbar ? erg.grund : `Nicht hochgeladen – ${erg.grund}`);
        },
        // Outlook-Mail (`.msg`/`.eml`): Anhänge auf Nachfrage mit auspacken, wie in den Dokumenten.
        nachUpload: async (datei, dokument) => {
          mailDateien.push({ datei, dokument });
        },
      });
      for (const m of mailDateien) {
        const r = await nachMailDateiUploadMitFrage(m.datei, m.dokument);
        if (r?.fehler) auspackFehler.push(r.fehler);
      }
      if (res.erfolgreich > 0) {
        setToast({
          text: auspackFehler[0] ?? hinweisErfolg(ziel.name, res.erfolgreich),
          fehler: auspackFehler.length > 0,
        });
      }
    } catch (err) {
      ablage.weg(ids);
      setToast({ text: err instanceof Error ? err.message : "Upload fehlgeschlagen", fehler: true });
    } finally {
      laeuft.current = false;
    }
  };

  const ausPostfach = async (dt: DataTransfer) => {
    // Synchron lesen — nach dem ersten `await` ist der Inhalt weg.
    // Neues Outlook: nur EWS-Ids, die erst der Server übersetzt (s. `neuesOutlook.ts`).
    const outlook = ausNeuemOutlook(dt);
    const zug = outlook ? null : leseAblageZug(dt);
    if (!zug && !outlook) return;
    if ("grund" in ziel) {
      setToast({ text: ziel.grund, fehler: true });
      return;
    }
    const api = {
      starte: ablage.starte,
      scheitern: ablage.scheitern,
      laeuftWieder: ablage.laeuftWieder,
      fertig: (ids: string[]) => ablage.weg(ids),
    };
    // Mail mit Anhängen: vorher fragen, was abgelegt wird (User 03.11.2026).
    const zielOrt = { contactId: ziel.contactId, ort: { kategorie: KUNDENAKTE_KATEGORIE, ordner: ordnerName } };
    const erg = outlook
      ? await legeNeuesOutlookAb(outlook, (z) => legeZugAbMitAnhangFrage(z, zielOrt, api, () => undefined))
      : zug
        ? await legeZugAbMitAnhangFrage(zug, zielOrt, api, () => undefined)
        : null;
    if (erg && erg.fehlgeschlagen.length === 0) {
      setToast({ text: `${erg.meldung} Hier erscheint es, sobald die Akte gebucht ist.`, fehler: false });
    }
  };

  const ohneZiel = "grund" in ziel;
  const props: AkteAblageProps = {
    onDropFiles: (dateien, zielPfad) => void hochladen(dateien, zielPfad),
    dropFilesHinweis: ohneZiel ? ziel.grund : `Dateien nach ${wo} in die Kundenakte von ${ziel.name} hochladen`,
    fremdAblageHinweis: (typen) => {
      const art = fremdAblageArt(typen);
      if (!art) return null;
      if (ohneZiel) return ziel.grund;
      return `${fremdAblageWas(art)} nach ${wo} in die Kundenakte von ${ziel.name} ablegen`;
    },
    onDropFremd: (dt) => void ausPostfach(dt),
    kopfZeilen: (spalten) => (
      <AblagePlatzhalterZeilen
        spalten={spalten}
        eintraege={ablage.eintraege}
        onErneut={ablage.erneutVersuchen}
        kannErneut={ablage.kannErneut}
        onEntfernen={(id) => ablage.weg([id])}
      />
    ),
  };
  const anzeige = (
    <Toast
      open={!!toast}
      message={toast?.text ?? ""}
      tone={toast?.fehler ? "error" : "success"}
      duration={toast?.fehler ? 7000 : 5000}
      onClose={() => setToast(null)}
    />
  );
  return { props, anzeige };
}
